import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, MapPin, Award, MapPinned, Mail as MailIcon } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import ImageUploader from '@/components/ImageUploader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const INQUIRY_TYPES = ['HOA', 'Property Management', 'Commercial Project', 'Insurance Adjuster', 'Vendor Inquiry', 'Other'];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [photos, setPhotos] = useState([]);
  const [f, setF] = useState({
    full_name: '',
    company: '',
    phone: '',
    email: '',
    inquiry_type: '',
    property_address: '',
    city: '',
    state: '',
    zip_code: '',
    message: '',
  });
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    document.title = 'Contact Elite Roofing & Construction — Property Managers, HOA, Commercial';
    let m = document.head.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.setAttribute('content', 'Reach Elite Roofing & Construction for HOA, property management, commercial, insurance adjuster, or vendor inquiries.');
  }, []);

  const validate = () => {
    const e = {};
    ['full_name', 'phone', 'email', 'inquiry_type', 'message'].forEach((k) => { if (!f[k]) e[k] = 'Required'; });
    if (f.email && !/^\S+@\S+\.\S+$/.test(f.email)) e.email = 'Invalid email';
    if (f.message && f.message.length < 5) e.message = 'Please add more detail';
    return e;
  };

  const submit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) { toast.error('Please complete required fields'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, String(v));
      });
      (photos || []).slice(0, 5).forEach((file) => fd.append('photos', file));

      const res = await axios.post(`${BACKEND_URL}/api/contact/submit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res?.data?.message || 'Message received');
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 mx-auto max-w-screen-md px-4 sm:px-6 py-20 text-center" data-testid="contact-success">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Message received</h1>
          <p className="mt-3 text-slate-600 text-lg">We'll respond within 1 business day.</p>
          <div className="mt-8"><Link to="/"><Button className="bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white">Back to Home</Button></Link></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="contact-page">
      <Navbar />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(212_51%_25%)] via-[#13294a] to-[#0f172a] text-white">
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight max-w-3xl" data-testid="contact-hero-title">
              Contact Elite Roofing & Construction
            </motion.h1>
            <p className="mt-5 text-lg text-white/85 max-w-2xl leading-relaxed">
              Property manager? HOA board? Commercial inquiry? Use the form below. Homeowners looking for a free roof inspection — use our quick form on the homepage for faster service.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900 mb-5">Send a Message</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <CField label="Full Name" required error={errors.full_name}><Input data-testid="contact-name" value={f.full_name} onChange={(e) => u('full_name', e.target.value)} /></CField>
              <CField label="Company / Organization"><Input data-testid="contact-company" value={f.company} onChange={(e) => u('company', e.target.value)} /></CField>
              <CField label="Phone" required error={errors.phone}><Input type="tel" data-testid="contact-phone" value={f.phone} onChange={(e) => u('phone', e.target.value)} /></CField>
              <CField label="Email" required error={errors.email}><Input type="email" data-testid="contact-email" value={f.email} onChange={(e) => u('email', e.target.value)} /></CField>
              <CField label="Inquiry Type" required error={errors.inquiry_type}>
                <Select value={f.inquiry_type} onValueChange={(v) => u('inquiry_type', v)}>
                  <SelectTrigger data-testid="contact-inquiry-type"><SelectValue placeholder="Select an option" /></SelectTrigger>
                  <SelectContent>{INQUIRY_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </CField>
            </div>
            <div className="mt-4">
              <AddressAutocomplete
                value={f.property_address}
                onChange={(text) => u('property_address', text)}
                onPlaceSelected={(p) => setF((s) => ({
                  ...s,
                  property_address: p.formatted || p.street || s.property_address,
                  city: p.city || s.city,
                  state: p.state || s.state,
                  zip_code: p.zip_code || s.zip_code,
                }))}
                label="Property Address"
                required={false}
                testId="contact-address"
                placeholder="Start typing the property address..."
              />
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-800 mb-1.5 block">City</Label>
                <Input data-testid="contact-city" value={f.city} onChange={(e) => u('city', e.target.value)} placeholder="City" />
              </div>
              <div>
                <Label className="text-slate-800 mb-1.5 block">State</Label>
                <Input data-testid="contact-state" value={f.state} onChange={(e) => u('state', e.target.value.toUpperCase())} maxLength={2} placeholder="FL" />
              </div>
              <div>
                <Label className="text-slate-800 mb-1.5 block">Zip Code</Label>
                <Input data-testid="contact-zip" value={f.zip_code} onChange={(e) => u('zip_code', e.target.value)} placeholder="ZIP" />
              </div>
            </div>
            <div className="mt-4">
              <CField label="Message" required error={errors.message}>
                <Textarea rows={5} data-testid="contact-message" value={f.message} onChange={(e) => u('message', e.target.value)} maxLength={4000} />
              </CField>
            </div>
            <div className="mt-4">
              <ImageUploader
                files={photos}
                onChange={setPhotos}
                maxFiles={5}
                maxMb={10}
                label="Attach photos (optional)"
                hint="Optional — photos of the property, damage, or scope. Up to 5 images, 10MB each."
                testId="contact-photos"
              />
            </div>
            <div className="mt-6">
              <Button onClick={submit} disabled={submitting} className="h-12 px-7 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white font-semibold" data-testid="contact-submit">
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            <SidebarCard icon={<Phone className="h-5 w-5" />} label="Phone"><a className="font-semibold text-[hsl(var(--primary))] hover:underline" href="tel:+14077850642">(407) 785-0642</a></SidebarCard>
            <SidebarCard icon={<MailIcon className="h-5 w-5" />} label="Email"><a className="font-semibold text-[hsl(var(--primary))] hover:underline" href="mailto:jeremy@elitercfl.com">jeremy@elitercfl.com</a></SidebarCard>
            <SidebarCard icon={<MapPin className="h-5 w-5" />} label="Office">
              <p className="text-slate-700">780 Academy Drive, Suite 102<br />Kissimmee, FL 34744</p>
            </SidebarCard>
            <SidebarCard icon={<Award className="h-5 w-5" />} label="License">
              <p className="text-slate-700">CGC032777<br /><span className="text-sm text-slate-500">Florida Certified General Contractor</span></p>
            </SidebarCard>
            <SidebarCard icon={<MapPinned className="h-5 w-5" />} label="Service Area">
              <p className="text-slate-700 text-sm">Osceola County &amp; Central Florida — Kissimmee, St. Cloud, Poinciana, Celebration, Orlando, Davenport.</p>
            </SidebarCard>
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function CField({ label, required, error, children }) {
  return (
    <div>
      <Label className="text-slate-800 mb-1.5 block">{label} {required && <span className="text-red-600">*</span>}</Label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function SidebarCard({ icon, label, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center gap-2 text-[hsl(var(--primary))] mb-2">
        {icon}
        <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-base">{children}</div>
    </div>
  );
}
